import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useTranslated } from '@/hooks/useTranslated';
import AuthenticatedBadge from '@/components/AuthenticatedBadge';
import ProductCard from '@/components/ProductCard';
import Lightbox from '@/components/Lightbox';
import { toast } from 'sonner';
import { Heart, ShoppingBag, ChevronRight, BadgeCheck, Truck, RefreshCw } from 'lucide-react';

export default function ProductDetail() {
  const { id } = useParams();
  const { t, api, addToCart, user } = useApp();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setData(r.data); setActiveImg(0); });
  }, [id, api]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 text-center font-light text-muted-foreground" data-testid="product-loading">Loading…</div>;
  const { product, similar } = data;
  return <ProductDetailInner product={product} similar={similar} t={t} api={api} user={user} addToCart={addToCart} navigate={navigate} activeImg={activeImg} setActiveImg={setActiveImg} />;
}

function ProductDetailInner({ product, similar, t, api, user, addToCart, navigate, activeImg, setActiveImg }) {
  const [tTitle, tDesc] = useTranslated([product.title, product.description], `prod:${product.id}:full`);
  const [zoom, setZoom] = React.useState(false);

  const handleAdd = () => { addToCart(product); toast.success('Added to your bag'); };
  const handleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    try { await api.post(`/wishlist/${product.id}`); toast.success('Saved to wishlist'); } catch { toast.error('Could not save'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="product-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 overline text-muted-foreground mb-10" data-testid="breadcrumb">
        <Link to="/" className="link-underline">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/shop?category=${product.category}`} className="link-underline">{product.category}</Link>
        <ChevronRight size={12} />
        <span className="text-foreground">{product.designer}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Image gallery */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] bg-muted overflow-hidden mb-4 relative group cursor-zoom-in" data-testid="active-image" onClick={() => setZoom(true)}>
            <img src={product.images?.[activeImg]} alt={product.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="overline text-white bg-black/40 px-3 py-1.5">Click to enlarge</span>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {(product.images || []).map((src, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden bg-muted ${i === activeImg ? 'ring-1 ring-foreground' : ''}`} data-testid={`thumb-${i}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5">
          <AuthenticatedBadge size="lg" />
          <Link to={`/shop?designer=${encodeURIComponent(product.designer)}`} className="block mt-6 overline link-underline" data-testid="designer-link">{product.designer}</Link>
          <h1 className="font-display text-3xl sm:text-4xl mt-3 leading-tight" data-testid="product-title">{tTitle}</h1>

          <div className="mt-6 flex items-baseline gap-4">
            <span className="font-display text-3xl" data-testid="product-price">${Number(product.price).toLocaleString()}</span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-sm text-muted-foreground line-through">{t('product.was')} ${Number(product.original_price).toLocaleString()}</span>
            )}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 border-t border-b border-border py-6 text-sm">
            <div><div className="overline text-muted-foreground">{t('product.condition')}</div><div className="mt-1 font-light">{product.condition}</div></div>
            {product.size && <div><div className="overline text-muted-foreground">{t('product.size')}</div><div className="mt-1 font-light">{product.size}</div></div>}
            {product.material && <div><div className="overline text-muted-foreground">{t('product.material')}</div><div className="mt-1 font-light">{product.material}</div></div>}
            {product.color && <div><div className="overline text-muted-foreground">Color</div><div className="mt-1 font-light">{product.color}</div></div>}
            {product.measurements && <div className="col-span-2"><div className="overline text-muted-foreground">{t('product.measurements')}</div><div className="mt-1 font-light">{product.measurements}</div></div>}
            {product.sku && <div><div className="overline text-muted-foreground">{t('product.sku')}</div><div className="mt-1 font-light">{product.sku}</div></div>}
          </div>

          <div className="mt-8 flex gap-3">
            <button onClick={handleAdd} className="flex-1 bg-foreground text-background px-8 py-4 overline inline-flex items-center justify-center gap-2 hover:opacity-80 transition" data-testid="add-to-cart-btn">
              <ShoppingBag size={14} /> {t('cta.addCart')}
            </button>
            <button onClick={handleWishlist} className="border border-foreground p-4 hover:bg-foreground hover:text-background transition" data-testid="wishlist-btn">
              <Heart size={16} strokeWidth={1.3} />
            </button>
          </div>

          <div className="mt-10">
            <div className="overline mb-3">{t('product.description')}</div>
            <p className="text-sm font-light leading-relaxed text-muted-foreground" data-testid="product-description">{tDesc}</p>
          </div>

          <div className="mt-10 space-y-4 border-t border-border pt-6">
            <div className="flex items-start gap-3"><BadgeCheck size={18} strokeWidth={1.3} className="text-accent mt-0.5" /><div><div className="overline">Authenticated</div><div className="text-xs font-light text-muted-foreground mt-1">Inspected & certified by our expert team.</div></div></div>
            <div className="flex items-start gap-3"><Truck size={18} strokeWidth={1.3} className="text-accent mt-0.5" /><div><div className="overline">Complimentary Shipping</div><div className="text-xs font-light text-muted-foreground mt-1">Insured, signature required.</div></div></div>
            <div className="flex items-start gap-3"><RefreshCw size={18} strokeWidth={1.3} className="text-accent mt-0.5" /><div><div className="overline">14-Day Returns</div><div className="text-xs font-light text-muted-foreground mt-1">Hassle-free returns on most items.</div></div></div>
          </div>
        </div>
      </div>

      {/* Similar */}
      {similar?.length > 0 && (
        <section className="mt-32 border-t border-border pt-16" data-testid="similar-section">
          <h2 className="font-display text-3xl sm:text-4xl mb-10">{t('product.similar')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {similar.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
      {zoom && <Lightbox images={(product.images || []).map((u) => ({ url: u, label: product.title }))} startIndex={activeImg} onClose={() => setZoom(false)} />}
    </div>
  );
}
